/**
 * ZEKKA NOTE: This is a somewhat hacky Claude-generated implementation of JSONL streaming.
 * It expects to be wired up to types that use the CareStateStreamingProtocolEventT output type.
 */

import { z } from 'zod';

import { getAccessToken } from '../../api/lotus/client';
import { ENV } from '../../config';

/**
 * Options for creating a JSONL stream.
 */
export interface JsonlStreamOptions {
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
  /** HTTP method; existing event streams default to GET. */
  method?: 'GET' | 'POST';
  /** JSON body for POST streams. */
  body?: unknown;
}

/**
 * Creates an authenticated GET request for streaming endpoints.
 *
 * @param path - API path (e.g., `/greenfield/care/{userId}/state/events/streaming`)
 * @param params - Query parameters
 * @param options - Request options including AbortSignal
 * @returns Fetch Response
 * @throws Error if user is not authenticated or request fails
 */
async function createStreamingRequest(
  path: string,
  params: Record<string, string | number | null>,
  options: JsonlStreamOptions = {}
): Promise<Response> {
  const token = await getAccessToken();
  if (token === null) {
    throw new Error('Cannot create streaming request: user is not authenticated');
  }

  // Build URL with query params
  const url = new URL(path, ENV.apiBaseUrl);
  for (const [key, value] of Object.entries(params)) {
    if (value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers: {
      Authorization: `Bearer firebase:${token}`,
      Accept: 'application/jsonl',
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(`Streaming request failed: ${response.status} ${response.statusText}`);
  }

  return response;
}

/**
 * Parses a JSONL stream into individual JSON objects.
 *
 * @param response - Fetch Response with JSONL body
 * @yields Parsed JSON objects
 */
async function* parseJsonlStream(response: Response): AsyncGenerator<unknown, void, unknown> {
  const body = response.body;
  if (body === null) {
    throw new Error('Response body is null');
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      let lineEnd: number;
      while ((lineEnd = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, lineEnd);
        buffer = buffer.slice(lineEnd + 1);

        if (line.trim()) {
          yield JSON.parse(line);
        }
      }
    }

    // Process any remaining content
    if (buffer.trim()) {
      yield JSON.parse(buffer);
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Streaming error with details from server.
 */
export class StreamingError extends Error {
  readonly details?: { exception?: string; trace?: string };

  constructor(message: string, details?: { exception?: string; trace?: string }) {
    super(message);
    this.name = 'StreamingError';
    this.details = details;
  }
}

/**
 * Checks if an error is an AbortError (from fetch or ReadableStream).
 */
function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Streams data events from a JSONL endpoint.
 *
 * This function handles all streaming protocol details internally:
 * - `ping` events are silently consumed (keep-alive)
 * - `disconnect` events cause the generator to return cleanly
 * - `error` events are thrown as StreamingError
 * - Abort signals cause the generator to return cleanly (no error thrown)
 * - Data events are validated with the provided Zod schema and yielded
 *
 * The caller receives only validated data events. To handle reconnection,
 * the caller should wrap this in a loop and restart on generator completion.
 *
 * @param path - API path for the streaming endpoint
 * @param params - Query parameters
 * @param dataEventSchema - Zod schema for validating data events
 * @param options - Request options including AbortSignal
 * @yields Validated data events
 * @throws StreamingError if server sends an error event
 * @throws ZodError if an event fails schema validation
 */
export async function* streamJsonlEvents<T>(
  path: string,
  params: Record<string, string | number | null>,
  dataEventSchema: z.ZodType<T>,
  options: JsonlStreamOptions = {}
): AsyncGenerator<T, void, unknown> {
  let response: Response;

  try {
    response = await createStreamingRequest(path, params, options);
  } catch (err: unknown) {
    // If aborted during request setup, exit cleanly
    if (isAbortError(err) || options.signal?.aborted) {
      return;
    }
    throw err;
  }

  try {
    for await (const rawEvent of parseJsonlStream(response)) {
      // Check if aborted before processing each event
      if (options.signal?.aborted) {
        return;
      }

      // Try to identify the event type
      if (typeof rawEvent !== 'object' || rawEvent === null) {
        throw new StreamingError(
          `Invalid streaming event: expected object, got ${typeof rawEvent}`
        );
      }

      const eventObj = rawEvent as Record<string, unknown>;
      const eventType = eventObj.type;

      // Handle protocol events internally
      switch (eventType) {
        case 'ping':
          // Keep-alive signal, silently consume
          continue;

        case 'disconnect':
          // Server requests reconnection - end the generator cleanly
          // Caller can reconnect if desired
          return;

        case 'error': {
          // Server sent an error - throw it
          const error = eventObj.error as Record<string, unknown> | undefined;
          const message =
            typeof error?.message === 'string' ? error.message : 'Unknown streaming error';
          throw new StreamingError(message, {
            exception: typeof error?.exception === 'string' ? error.exception : undefined,
            trace: typeof error?.trace === 'string' ? error.trace : undefined,
          });
        }

        default: {
          // Validate as a data event and yield
          const validated = dataEventSchema.parse(rawEvent);
          yield validated;
        }
      }
    }
  } catch (err: unknown) {
    // If aborted during streaming, exit cleanly
    if (isAbortError(err) || options.signal?.aborted) {
      return;
    }
    throw err;
  }
}
